import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { getCurrentProfile, getGrantedBoxes, isBoxAccount } from "./utils/userProfile";
import { useBoxStaff } from "./utils/useBoxStaff";
import { isPathHiddenForMe } from "./utils/accountBuiltIns";
import PartnerNavbar, { PartnerTabs } from "./partner/PartnerNavbar";

const TEHAN_USERNAME = "karina@123";

// صفحات ارت تاتش - نفس روابط حساب الشريك (طلب جديد وبيع مباشر صفحة وحدة)
const ART_TOUCH_PAGES = [
  { label: "الرئيسية", path: "/partner" },
  { label: "إضافة طلب", path: "/partner-order" },
  { label: "حساب سعر اللوحة", path: "/partner-price" },
];
const ART_TOUCH_PATHS = ["/partner", "/partner-order", "/partner-add", "/partner-price"];

const NavButton = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="px-1.5 py-1 rounded text-[13px] font-medium whitespace-nowrap hover:bg-white/15 transition-colors"
  >
    {children}
  </button>
);

// قسم مطوي داخل القائمة (مثل الموظفات السابقات) - ينفتح بضغطة
const CollapsibleItems = ({ section, onNavigate, itemClassName, headerClassName }) => {
  const [expanded, setExpanded] = useState(false);
  if (!section?.items?.length) return null;
  return (
    <>
      <button onClick={() => setExpanded((v) => !v)} className={headerClassName}>
        {section.title} ({section.items.length}) {expanded ? "▲" : "▼"}
      </button>
      {expanded &&
        section.items.map((item) => (
          <button key={item.path} onClick={() => onNavigate(item.path)} className={itemClassName}>
            {item.label}
          </button>
        ))}
    </>
  );
};

const NavDropdown = ({ label, open, onToggle, items, onNavigate, collapsedSection }) => (
  <div className="relative">
    <button
      onClick={onToggle}
      className="flex items-center gap-0.5 px-1.5 py-1 rounded text-[13px] font-medium whitespace-nowrap hover:bg-white/15 transition-colors"
    >
      {label}
      <ChevronDown
        size={14}
        className={`transition-transform ${open ? "rotate-180" : ""}`}
      />
    </button>

    {open && (
      <div className="absolute right-0 mt-1 bg-white text-black rounded-md shadow-lg w-48 py-1 z-50">
        {items.map((item) => (
          <button
            key={item.path}
            onClick={() => onNavigate(item.path)}
            className="block w-full text-right px-4 py-2 text-sm hover:bg-gray-100"
          >
            {item.label}
          </button>
        ))}
        <CollapsibleItems
          section={collapsedSection}
          onNavigate={onNavigate}
          headerClassName="block w-full text-right px-4 py-2 text-xs text-gray-500 border-t hover:bg-gray-100"
          itemClassName="block w-full text-right px-6 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
        />
      </div>
    )}
  </div>
);

const MobileSection = ({ title, items, onNavigate, collapsedSection }) => (
  <div className="border-t border-white/20 pt-2 mt-2 first:border-t-0 first:mt-0 first:pt-0">
    {title && (
      <span className="block font-bold text-sm text-white/70 mb-1">
        {title}
      </span>
    )}
    <div className="flex flex-col gap-1">
      {items.map((item) => (
        <button
          key={item.path}
          onClick={() => onNavigate(item.path)}
          className="text-right rounded-md px-2 py-2 hover:bg-white/10"
        >
          {item.label}
        </button>
      ))}
      <CollapsibleItems
        section={collapsedSection}
        onNavigate={onNavigate}
        headerClassName="text-right rounded-md px-2 py-2 text-sm text-white/70 hover:bg-white/10"
        itemClassName="text-right rounded-md px-4 py-2 text-white/80 hover:bg-white/10"
      />
    </div>
  </div>
);

const Navbar = ({ username, onLogout }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [partnersOpen, setPartnersOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  const closeAllDropdowns = () => {
    setPartnersOpen(false);
    setStaffOpen(false);
    setInvoiceOpen(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setOpen(false);
    closeAllDropdowns();
  };

  const boxStaff = useBoxStaff();
  // تيهان (karina@123) زرها بشريط المدير مو ضمن الموظفات - لوحتها حسب نوع حسابها
  const tehanPath = boxStaff.some((s) => s.username === TEHAN_USERNAME)
    ? `/workspace/${TEHAN_USERNAME}`
    : "/karina";
  const pages = [];
  const canAdd = ["rand@123", "jumana@123", "sara@123", "Baneen@123", "maryam@123", "karina@123", "farah@123", "maysa@123"].includes(username);

  if (isBoxAccount()) {
    // حسابات البوكسات (الجديدة، أو قديمة انحوّلت - مثل كارينا ← تيهان): لوحتها
    // بس - البوكسات نفسها أزرار داخل اللوحة. قبل كل الفحوصات بالأسماء الثابتة
    pages.push({ label: getCurrentProfile().displayName || "لوحتي", path: "/workspace" });
  } else if (username === "admin") {
    pages.push(
      { label: "المدير", path: "/admin" },
      { label: "بنين", path: "/Baneen" },
      { label: "المصمم", path: "/designer" },
      { label: "المطبعة", path: "/print" },
      { label: "الرسام", path: "/mohamed" },
      { label: "الورشة", path: "/burhan" },
      { label: "تيهان", path: tehanPath },
    );
  } else if (canAdd) {
    if (username === "rand@123") pages.push({ label: "رند", path: "/rand" }, { label: "إنشاء فاتورة", path: "/invoice" }, { label: "الفواتير", path: "/invoices" }, { label: "عروض الأسعار", path: "/proposals" });
    if (username === "jumana@123") pages.push({ label: "جمانا", path: "/jumana" });
    if (username === "sara@123") pages.push({ label: "ساره", path: "/sara" });
    if (username === "maryam@123") pages.push({ label: "مريم", path: "/maryam" }, { label: "إنشاء فاتورة", path: "/invoice" }, { label: "الفواتير", path: "/invoices" }, { label: "عروض الأسعار", path: "/proposals" });
    if (username === "farah@123") pages.push({ label: "فرح", path: "/farah-dashboard" }, { label: "إنشاء فاتورة", path: "/invoice" }, { label: "الفواتير", path: "/invoices" }, { label: "عروض الأسعار", path: "/proposals" });
    if (username === "karena@123") pages.push({ label: "تيهان", path: "/karena" });
    if (username === "maysa@123") pages.push({ label: "مياسة", path: "/maysa" }, { label: "إنشاء فاتورة", path: "/invoice" }, { label: "الفواتير", path: "/invoices" }, { label: "عروض الأسعار", path: "/proposals" });
    if (username === "Baneen@123") pages.push(
      { label: "بنين", path: "/Baneen" },
      { label: "مياسة", path: "/maysa" },
      { label: "المصمم", path: "/designer" },
      { label: "المطبعة", path: "/print" },
      { label: "الورشة", path: "/burhan" },
      { label: "الرسام", path: "/mohamed" },
      { label: "جمانا", path: "/jumana" }
      // الفواتير/عروض الأسعار صارت أزرار داخل لوحة بنين (BaneenDashboard.js) -
      // التول بار للواجهات/الموظفين فقط
    );
    // 🔒 زر "إضافة طلب" مخفي مؤقتاً من التول بار (الطلب يُنشأ الآن تلقائياً
    // عبر اعتماد الفاتورة اللي توجّه لـ/add برمجياً - راجع InvoiceViewPage.jsx's
    // handleApprove). المسار /add نفسه ما زال شغال، فقط الزر مخفي - رجّعه
    // بإزالة التعليق إذا احتجنا الإدخال اليدوي مرة ثانية.
    // pages.push({ label: "إضافة طلب", path: "/add" });
  } else if (username === "designer@123") {
    pages.push({ label: "المصمم", path: "/designer" });
  } else if (username === "print@123") {
    pages.push({ label: "المطبعة", path: "/print" });
  } else if (username === "art@1234") {
    pages.push({ label: "الورشة", path: "/burhan" });
  } else if (username === "mohamed@123" || username === "halima@123") {
    pages.push({
      label: "الرسام",
      path: username === "mohamed@123" ? "/mohamed" : "/halima",
    });
  } else if (username === "anwar@123") {
    pages.push({ label: "أنوار", path: "/anwar" });
  } else if (username === "arttouch@123") {
    pages.push(
      { label: "الرئيسية", path: "/partner" },
      // طلب جديد وبيع مباشر صاروا صفحة وحدة بيها اختيار (radio) بين الاثنين
      { label: "إضافة طلب", path: "/partner-order" },
      { label: "حساب سعر اللوحة", path: "/partner-price" }
    );
  } else if (username === "mitham@123") {
    pages.push({ label: "ميثم برحيّه", path: "/partner-mitham" });
  } else if (username === "osama@123") {
    pages.push({ label: "المحاسب", path: "/accountant" });
  } else if (username === "ms.sara") {
    pages.push({ label: "ست سارة", path: "/partner-sara" });
  }

  // صلاحيات إضافية ممنوحة من واجهة "صلاحية المستخدمين" (أدمن) - تُضاف فوق
  // أزرار المستخدم الثابتة أعلاه بدون تكرار، بدون أي تعديل على المنطق الحالي
  const grantedDashboards = JSON.parse(
    localStorage.getItem("grantedDashboards") || "[]"
  );
  grantedDashboards.forEach((d) => {
    if (!pages.some((p) => p.path === d.path)) {
      pages.push(d);
    }
  });

  // البوكسات الإضافية الممنوحة لحساب قديم (حسابات البوكسات أضيفت فوق)
  if (!isBoxAccount()) {
    getGrantedBoxes(getCurrentProfile().permissions)
      .filter((box) => box.path && !pages.some((p) => p.path === box.path))
      .forEach((box) => pages.push({ label: box.label, path: box.path }));
  }

  // ترتيب ثابت لأزرار بنين (يشمل الواجهات الممنوحة إلها مثل تيهان/ارت تاتش
  // اللي تنضاف فوق) - أي زر مو بالقائمة يبقى بآخر الشريط بنفس ترتيبه
  if (username === "Baneen@123") {
    const BANEEN_ORDER = ["بنين", "تيهان", "المصمم", "المطبعة", "الورشة", "الرسام", "مياسة", "جمانا", "ارت تاتش"];
    const rank = (p) => {
      const i = BANEEN_ORDER.indexOf(String(p.label).trim());
      return i === -1 ? BANEEN_ORDER.length : i;
    };
    pages.sort((a, b) => rank(a) - rank(b));
  }

  // الأزرار الثابتة اللي المدير شالها من صلاحية المستخدمين (utils/accountBuiltIns.js)
  if (username !== "admin") {
    for (let i = pages.length - 1; i >= 0; i--) {
      if (isPathHiddenForMe(pages[i].path.split("?")[0])) pages.splice(i, 1);
    }
  }

  // ✅ نفس الوجهات لكل الأزرار/القوائم الخاصة بالمدير، مبنية مرة وحدة
  // ونستخدمها بالعرض العادي (dropdowns) وبقائمة الموبايل بنفس الوقت
  const invoiceItems = [
    { label: "الفواتير", path: "/invoices" },
    { label: "انشاء فاتورة", path: "/invoice" },
  ];

  const partnerItems = [
    { label: "ميثم", path: "/partner-mitham" },
    // ارت تاتش: زر واحد - صفحاتها (إضافة طلب/حساب السعر) بشريط داخل واجهتها
    { label: "ارت تاتش", path: "/partner" },
    { label: "ست سارة", path: "/partner-sara" },
  ];

  const staffItems = [
    // موظفات البوكسات (الحسابات الجديدة) - تلقائياً من واجهة صلاحية المستخدمين
    ...boxStaff
      .filter((s) => s.username !== TEHAN_USERNAME)
      .map((s) => ({ label: s.displayName, path: `/workspace/${s.username}` })),
    { label: "المحاسب", path: "/accountant" },
  ];

  // الموظفات السابقات: لوحاتهم باقية (ما انحذفت) بس مطوية بقسم منفصل
  const formerStaffSection = {
    title: "📁 الموظفات السابقات",
    items: [
      { label: "رند", path: "/rand" },
      { label: "سارة", path: "/sara" },
      { label: "انوار", path: "/anwar" },
      { label: "جمانا", path: "/jumana" },
      { label: "فرح", path: "/farah-dashboard" },
      { label: "مياسة", path: "/maysa" },
    ],
  };

  const isAdmin = username === "admin";

  // حساب الشريك ارت تاتش: نفس الروابط بشريط بهوية Artika (تصميم فقط)
  if (username === "arttouch@123") {
    return <PartnerNavbar pages={pages} onLogout={onLogout} />;
  }

  return (
    <nav className="bg-blue-600 text-white w-full shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center gap-3">
        <h1 className="text-base sm:text-lg font-bold whitespace-nowrap">
          نظام تتبع الطلبات
        </h1>

        <div className="md:hidden">
          <button onClick={() => setOpen(!open)} className="focus:outline-none p-1">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        <div className="hidden md:flex flex-wrap justify-end gap-x-0.5 gap-y-1 items-center">
          {pages.map((page) => (
            <NavButton key={page.path} onClick={() => handleNavigate(page.path)}>
              {page.label}
            </NavButton>
          ))}

          {isAdmin && (
            <NavDropdown
              label="الفواتير"
              open={invoiceOpen}
              onToggle={() => {
                setInvoiceOpen(!invoiceOpen);
                setPartnersOpen(false);
                setStaffOpen(false);
              }}
              items={invoiceItems}
              onNavigate={handleNavigate}
            />
          )}

          {isAdmin && (
            <NavButton onClick={() => handleNavigate("/proposals")}>
              عروض الأسعار
            </NavButton>
          )}

          {isAdmin && (
            <NavDropdown
              label="الشركاء"
              open={partnersOpen}
              onToggle={() => {
                setPartnersOpen(!partnersOpen);
                setInvoiceOpen(false);
                setStaffOpen(false);
              }}
              items={partnerItems}
              onNavigate={handleNavigate}
            />
          )}

          {isAdmin && (
            <NavButton onClick={() => handleNavigate("/accounting")}>
              🏛️ النظام المحاسبي الذكي
            </NavButton>
          )}

          {isAdmin && (
            <NavDropdown
              label="الموظفات"
              open={staffOpen}
              onToggle={() => {
                setStaffOpen(!staffOpen);
                setInvoiceOpen(false);
                setPartnersOpen(false);
              }}
              items={staffItems}
              collapsedSection={formerStaffSection}
              onNavigate={handleNavigate}
            />
          )}

          {isAdmin && (
            <NavButton onClick={() => handleNavigate("/canvas-cost")}>
              حساب تكلفة اللوحة
            </NavButton>
          )}

          <button
            onClick={onLogout}
            className="text-red-500 bg-white px-2.5 py-1 rounded text-[13px] font-bold hover:bg-red-100 whitespace-nowrap"
          >
            تسجيل الخروج
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-blue-700 px-4 pb-4 max-h-[75vh] overflow-y-auto">
          <MobileSection items={pages} onNavigate={handleNavigate} />

          {isAdmin && (
            <MobileSection title="الفواتير" items={invoiceItems} onNavigate={handleNavigate} />
          )}

          {isAdmin && (
            <MobileSection
              title="عروض الأسعار"
              items={[{ label: "عروض الأسعار", path: "/proposals" }]}
              onNavigate={handleNavigate}
            />
          )}

          {isAdmin && (
            <MobileSection title="الشركاء" items={partnerItems} onNavigate={handleNavigate} />
          )}

          {isAdmin && (
            <MobileSection
              items={[{ label: "🏛️ النظام المحاسبي الذكي", path: "/accounting" }]}
              onNavigate={handleNavigate}
            />
          )}

          {isAdmin && (
            <MobileSection
              title="الموظفات"
              items={staffItems}
              collapsedSection={formerStaffSection}
              onNavigate={handleNavigate}
            />
          )}

          {isAdmin && (
            <MobileSection
              items={[{ label: "حساب تكلفة اللوحة", path: "/canvas-cost" }]}
              onNavigate={handleNavigate}
            />
          )}

          <div className="border-t border-white/20 pt-3 mt-2">
            <button
              onClick={onLogout}
              className="w-full text-center text-red-500 bg-white px-3 py-2 rounded-md font-bold hover:bg-red-100"
            >
              تسجيل الخروج
            </button>
          </div>
        </div>
      )}

      {/* المدير داخل واجهة ارت تاتش: نفس روابط الشريك بشريط تحت الناف بار */}
      {isAdmin && ART_TOUCH_PATHS.includes(pathname) && (
        <div className="bg-white text-black border-b" dir="rtl">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-1 overflow-x-auto">
            <span className="text-sm font-bold text-gray-500 ml-2 whitespace-nowrap">ارت تاتش:</span>
            <PartnerTabs pages={ART_TOUCH_PAGES} />
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
